import axios from "axios";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { InfinitySpin } from "react-loader-spinner";
import { UserContext } from "../../../Context/Context";
import LoginProcess from "./LoginProcess";


const AuthRedirect = () => {
  const { setIsAuthenticated } = useContext(UserContext);
  const isAuthenticated = useIsAuthenticated();
  const { instance, inProgress } = useMsal();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState({
    ssoStatus: true,
    loader: false
  });
  const [redirectError, setRedirectError] = useState("");
  const [hasStartedSsoFlow, setHasStartedSsoFlow] = useState(false);

  const navigateToLoginWithError = (message) => {
    const safeMessage = message || "SSO login failed. Please try again.";
    sessionStorage.setItem("ssoAuthErrorMessage", safeMessage);
    setRedirectError(safeMessage);
    setTimeout(() => {
      navigate("/", { replace: true });
    }, 1800);
  };

  const getSsoErrorMessage = (error) => {
    const isTimeoutError =
      error?.code === "ECONNABORTED" ||
      error?.response?.status === 408 ||
      error?.response?.status === 504 ||
      String(error?.message || "").toLowerCase().includes("timeout");

    if (isTimeoutError) return "Connection Time out try gain later.";
    if (error?.response?.data?.message) return error.response.data.message;
    if (error?.response?.data?.result) return error.response.data.result;
    if (error?.message) return error.message;
    return "Unable to complete Single Sign-On. Please try again.";
  };

  const fetchData = async () => {
    try {
      let currentAccount = instance.getActiveAccount();

      if (!currentAccount) {
        const allAccounts = instance.getAllAccounts();
        if (allAccounts?.length > 0) {
          currentAccount = allAccounts[0];
          instance.setActiveAccount(currentAccount);
        }
      }

      if (!currentAccount) {
        navigateToLoginWithError("Could not identify your Microsoft account. Please try signing in again.");
        return;
      }

      const silentRequest = {
        account: currentAccount,
        scopes: [
          "openid",   
          "profile",
          "email",
          "offline_access",
          "api://63430915-ea32-40c9-9644-835cd749c3e3/access_as_user"]
      };

      const response = await instance.acquireTokenSilent(silentRequest);
      const tokenS = response.accessToken;

      if (!tokenS) {
        navigateToLoginWithError("Could not acquire SSO token. Please try signing in again.");
        return;
      }

      const getBrowserTimeZone = () => {
        const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        return userTimeZone;
      };
      const browserTimeZone = getBrowserTimeZone();

      var url = `${process.env.REACT_APP_API_URL_DOTNET}/Sso/validate-azure-token?ClientTimeZone=${browserTimeZone}`;
      const headers = {
        Authorization: `Bearer ${tokenS}`,
      };
      const loginResponse = await axios.get(url, { headers, timeout: 60000 });
      if (loginResponse?.data?.success == 200 && loginResponse?.data?.result) {
        LoginProcess(loginResponse.data.result, setIsLoading, setIsAuthenticated, isLoading, navigate);
      } else {
        navigateToLoginWithError(loginResponse?.data?.message || "SSO validation failed. Please try again.");
      }
    } catch (error) {
      navigateToLoginWithError(getSsoErrorMessage(error));
    }
  };

  useEffect(() => {
    if (hasStartedSsoFlow) return;
    if (inProgress !== InteractionStatus.None) return;

    const account = instance.getActiveAccount() || instance.getAllAccounts()?.[0];
    const shouldAttemptSso = Boolean(account) || isAuthenticated;

    if (shouldAttemptSso) {
      setHasStartedSsoFlow(true);
      fetchData();
      return;
    }

    navigateToLoginWithError("Session expired before SSO could complete. Please sign in again.");
  }, [hasStartedSsoFlow, inProgress, isAuthenticated, instance]);

  return (
    <div className="d-flex flex-column align-items-center justify-content-center vh-100 vw-100">
      <InfinitySpin className="d-flex align-items-center justify-content-center" color="#ffc107" />
      {redirectError && <p className="sign-up-warning mt-3 mb-0">{redirectError}</p>}
    </div>
  );
};

export default AuthRedirect;
